'use strict';
// import {extendPrototypeResult as localforage} from 'localforage-indexes';
import isDate from 'lodash/isDate';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import moment from 'moment/moment';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/mapTo';
import 'rxjs/add/operator/skip';
import {buildInjector} from '../../_libs/angular-utils';
import {applyAutoFilter, ReduxList} from '../../_libs/redux-observable-list';
import {getCollection, getLink} from '../../core-utilities/domain/ReduxUtils';
import {FilterModalOptions} from '../../core-utilities/filters/FilterModalOptions';
import {sortByPropertyAccessor} from '../../core-utilities/filters/filterUtils';
import {SortMethod} from '../../core-utilities/filters/SortMethod';
import {SYNC_PATH} from '../../core-utilities/rx-utils';
import {groupByDate} from '../../components/lists/DateHeader';
import {BASE_REDUX_LIST_OPTIONS} from '../../config/redux-list';
import {buildUserChannelSyncNotifier} from '../../auth/utils/redux-lists';
import {COLLECTION as TASK_COLLECTION} from '../../tasks/domain/TaskDomain';
import {COLLECTION as ASSET_TASK_COLLECTION} from '../../tasks/domain/AssetTaskDomain';
import {timeOffset} from '../../tasks/domain/utils';
import {removeByAssignmentStatus, removeByStatus} from '../../tasks/filters/TasksFilterService';
import {DateFilterDialogController} from '../filters/DateFilterDialogController';
import filterTemplate from '../filters/priorities-list-filter.html';
import {USER_PRIORITIES_COLLECTION} from '../domain/config';
import {logger} from './logger';

// load angular dependencies:
const dependencies = {};
export const injector = buildInjector(dependencies, [
  'FirebaseUtil',
]);

// disabling assignedByUser until server can support it properly
export const defaultFilterOptions = {
  user: {
    assignedToAny: true,
    // assignedByUser: true,
    assignedToNone: true,
  },
  status: {
    cancelled: true,
    has_rejection: true,
    rejected: true,
    pending: true,
    in_progress: true,
    submitted: true,
    approved: true,
  },
  scheduleStatus: {
    complete: true,
    cancelled: true,
    past_due: true,
    imminent: true,
    scheduled: true,
    not_scheduled: true,
  },
  dateRange: {
    useAfter: false,
    // afterDate: new Date(),
    useBefore: false,
    // beforeDate: new Date(),
  },
};

export const defaultSortOptions = {
  currentMethod: SortMethod.DESCENDING.name,
  property: 'date',
};

// TODO: replace next API with standard after first release cycle
const resourcePath = `next/${USER_PRIORITIES_COLLECTION}`;

const autoFilterProperties = [
  'target.name',
  'target.assignedTo.name',
  'metadata.parentName',
  'metadata.assetId',
  'metadata.organizationName',
];

export function buildList(options, targetOptions) {
  const matchesAutoFilter = applyAutoFilter(autoFilterProperties, 1);

  return new ReduxList({
    ...BASE_REDUX_LIST_OPTIONS,
    pageSize: 100,
    hasParentId: false,
    useDelete: true,
    useFiltering: true,
    useAutoFilter: true,
    useFilterOptions: true,
    useSortOptions: true,
    defaultFilterOptions,
    defaultSortOptions,
    isVisible,
    applySortOptions,
    syncNotifier: buildUserChannelSyncNotifier('priorities', `syncNotifier priorities=${targetOptions.priority}`),
    listPath: resourcePath,
    listSyncPath: `${resourcePath}/${SYNC_PATH}`,
    deleteItemPath: itemId => `${USER_PRIORITIES_COLLECTION}/${itemId}`,
    logger,
    ...options,

    // cacheOptions: {
    //   localForage: asyncStorage,
    //   key: targetOptions.cacheName,
    // },
  });

  function isVisible(item, filterData) {
    if (!isObject(item) || !isObject(item.target)) return false;
    if (!matchesAutoFilter(item, filterData.filter)) return false;
    try {
      const options = filterData.filterOptions;
      const target = item.target;
      const targetCollection = getCollection(target);

      if (removeByStatus(target, filterData.filterOptions)) return false;

      switch (targetCollection) {
        case ASSET_TASK_COLLECTION:
        case TASK_COLLECTION:
          if (removeByAssignmentStatus(target, options)) return false;
          if (removeByUserAssignment(target, options/* , rootState*/)) return false;
          break;
      }

      return true;
    } catch (err) {
      logger.error({err, item, options}, 'error with isVisible');
      return false;
    }
  }
}

// TODO: needs current user ID from redux
export function removeByUserAssignment(task, options/* , rootState*/) {
  try {
    if (options.user.assignedToAny && getLink(task, 'assignedTo').id) return false;

    if (options.user.assignedToNone && !getLink(task, 'assignedTo').id) return false;

    // const authState = getAuthState(rootState);
    // const userId = authState ? authState.userId : null;
    // if (options.user.assignedByUser && userId === getLink(task, 'scheduledBy').id) return false;

    return true;
  } catch (err) {
    logger.error({err}, 'An error occurred filtering the user results');
  }
}

export function applySortOptions(list, sortOptions) {
  return list.sort(sortByPropertyAccessor(getSortingDate, SortMethod.ASCENDING.val, true));
}

/**
 * Use the offset to sort scheduled dates
 * @param pin
 * @return {*}
 */
export function getSortingDate(pin) {
  return getDate(pin, true);
}

/**
 * Do not use the offset when displaying scheduled dates
 * @param pin
 * @return {*}
 */
export function getTargetDate(pin) {
  return getDate(pin, false);
}

function getDate(pin, useOffset) {
  if (pin) {
    const target = pin.target;
    const targetCollection = getCollection(target);
    switch (targetCollection) {
      case ASSET_TASK_COLLECTION:
      case TASK_COLLECTION:
        if (target) {
          const status = target.status;
          const isComplete = status === 'approved' || status === 'submitted';
          const isCancelled = status === 'cancelled';

          if (isComplete && isNumber(target.submittedOn)) {
            return target.submittedOn;
          } else if (isComplete && isNumber(target.approvedOn)) {
            return target.approvedOn;
          } else if (isCancelled && isNumber(target.cancelledOn)) {
            return target.cancelledOn;
          } else {
            const time = isNumber(target.scheduled) ? target.scheduled : null;
            if (useOffset) {
              return time ? (target.useTime ? time : time + timeOffset - 1) : null;
            } else {
              return time;
            }
          }
        }

      /* fall-through */
      default:
        return target ? target.lastUpdated : pin.lastUpdated;
    }
  }
}

export const filterModalOptions = new FilterModalOptions({
  filterTemplate,
  controller: DateFilterDialogController,
  filterOptions: defaultFilterOptions,
  sortOptions: defaultSortOptions,
});

export const REDUX_OPTIONS = {
  applySortOptions,
  filterModalOptions,
  listTransform: groupByDate({
    dateFormatter,
    dateGetter: pin => {
      const date = getTargetDate(pin);
      if (!date) {
        return null;
      } else if (isNumber(date)) {
        return dateFormatter(date);
      } else if (isDate(date)) {
        return dateFormatter(date.getTime());
      } else {
        return null;
      }
    },
  }),
};

function dateFormatter(date) {
  const d = new Date(date);
  return moment([d.getFullYear(), d.getMonth()]).valueOf();
}



// WEBPACK FOOTER //
// ./src/js/priorities/redux/prioritiesListConfig.js